import type { PaletteNeutralScale } from '@/features/colors/color.types';

/**
 * The neutral ramps every palette draws from.
 *
 * Design specifies these once and repeats them verbatim on each palette sheet —
 * steps 0 and 800-950 are literally marked "shared" — so they live here rather
 * than being restated per palette. What varies between palettes is which ramp a
 * given color slot is *tied to* (warm vs cool), not the ramp values themselves.
 *
 * Two warm steps intentionally depart from the sheets, both tuned against what
 * they actually draw rather than read off the ramp: 50 is Summer's weekend cell
 * tint (GRID_WEEKEND_STEP) and 200 its gridlines (GRID_LINE_STEP). The sheets say
 * #F7F5F3 and #E8E4E0.
 *
 * The cool ramp is deliberately left unmirrored. Every other step pairs as an
 * exact reverse (warm #F0EDEA to cool #EAEDF0), and warm 50 used to: #F7F5F3 to
 * #F3F5F7. #F8F7F5 has no cool twin and is not getting one — these two steps were
 * tuned for the warm grid on its own terms, so a warm accent's weekend tint now
 * sits a touch lighter than a cool accent's by design. Don't "restore" the pairing.
 */

export const NEUTRAL_WARM: PaletteNeutralScale = {
  '0': '#FFFFFF',
  // Departs from the sheet's #F7F5F3 — see the note above.
  '50': '#F8F7F5',
  '100': '#F0EDEA',
  // Departs from the sheet's #E8E4E0 — see the note above.
  '200': '#E7E4E0',
  '300': '#E0DCD8',
  '400': '#C8C4BF',
  '500': '#9A948F',
  '600': '#6B6560',
  '700': '#5B554E',
  '800': '#2B2B2B',
  '900': '#202020',
  '950': '#0F0F0F',
};

export const NEUTRAL_COOL: PaletteNeutralScale = {
  '0': '#FFFFFF',
  '50': '#F3F5F7',
  '100': '#EAEDF0',
  '200': '#E0E4E8',
  '300': '#D8DCE0',
  '400': '#BFC4C8',
  '500': '#8F949A',
  '600': '#60656B',
  '700': '#4E555B',
  '800': '#2B2B2B',
  '900': '#202020',
  '950': '#0F0F0F',
};

/** Design's "True" column — achromatic, no warm or cool bias. */
export const NEUTRAL_GRAY: PaletteNeutralScale = {
  '0': '#FFFFFF',
  '50': '#F5F5F5',
  '100': '#EDEDED',
  '200': '#E4E4E4',
  '300': '#DCDCDC',
  '400': '#C4C4C4',
  '500': '#949494',
  '600': '#656565',
  '700': '#555555',
  '800': '#2B2B2B',
  '900': '#202020',
  '950': '#0F0F0F',
};
